import { useEffect, useState } from "react";
import styles from "./updatedAssetsList.module.css";
import { UpdatableAssetsList } from "../../models/IUpdateAssetsListResponse";

const PAGE_SIZE = 4;

type UpdatedAssetItem = UpdatableAssetsList & { isSelected: boolean };
interface UpdatedAssetsListProps {
  assetsList?: UpdatableAssetsList[] | null;
  selectedAssetList?: boolean[] | null;
  handleSelectAsset: (index: number) => void;
}

export default function UpdatedAssetsList({
  assetsList,
  selectedAssetList,
  handleSelectAsset,
}: Readonly<UpdatedAssetsListProps>) {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = assetsList?.length ? Math.max(1, Math.ceil(assetsList?.length / PAGE_SIZE)) : 1;
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleAssets = assetsList?.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (page: number) => {
    const clampedPage = Math.min(Math.max(page, 1), totalPages);
    setCurrentPage(clampedPage);
  };

  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(assetsList?.length ?? 0 / PAGE_SIZE));
    setCurrentPage((prev) => Math.min(prev, newTotalPages));
  }, [assetsList?.length]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <span className={styles.headerText}>File Name and Version Description</span>
        </div>

        <div className={styles.assetsList}>
          {visibleAssets?.map((asset, index) => (
            <UpdatedAssetItem
              key={asset.sourceAsset.assetVersionId}
              asset={asset}
              isSelected={selectedAssetList?.[index] ?? false}
              onSelect={() => {
                handleSelectAsset(index);
              }}
            />
          ))}
        </div>
      </div>
      <div className={styles.pagination}>
        <button
          className={styles.paginationButton}
          onClick={() => {
            handlePageChange(currentPage - 1);
          }}
          disabled={currentPage === 1}
        >
          ‹
        </button>
        {Array.from({ length: totalPages }, (_, index) => {
          const pageNumber = index + 1;
          const isActive = pageNumber === currentPage;
          return (
            <button
              key={pageNumber}
              className={`${styles.paginationButton} ${isActive ? styles.active : ""}`}
              onClick={() => handlePageChange(pageNumber)}
            >
              {pageNumber}
            </button>
          );
        })}
        <button
          className={styles.paginationButton}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          ›
        </button>
      </div>
    </>
  );
}

interface UpdatedAssetItemProps {
  asset: UpdatableAssetsList;
  isSelected: boolean;
  onSelect: () => void;
}

function UpdatedAssetItem({ asset, isSelected, onSelect }: Readonly<UpdatedAssetItemProps>) {
  let sourceAssetPath = `/api/Asset/${asset.sourceAsset.blobPath}`;
  let description = asset.sourceAsset.description;

  if (asset.sourceAsset.smartObject) {
    sourceAssetPath = `/api/Asset/${asset.sourceAsset.blobPath}/${asset.sourceAsset.smartObject.smartObjectSvg
      .toLowerCase()
      .replaceAll(" ", "_")}`;
    description = asset.sourceAsset.smartObject.smartObjectSvg;
  }

  return (
    <div className={`${styles.assetItem} ${isSelected ? styles.selected : ""}`}>
      <div className={styles.checkboxContainer}>
        <input type="checkbox" checked={isSelected} onChange={onSelect} className={styles.checkbox} />
      </div>

      <div className={styles.thumbnailContainer}>
        <img src={sourceAssetPath} alt={asset.sourceAsset.name} className={styles.thumbnail} />
      </div>

      <div className={styles.assetInfo}>
        <div className={styles.fileInfo}>
          <span>{asset.sourceAsset.name}</span>
          <span>
            <strong>Version:</strong> {asset.sourceAsset.displayVersion}
          </span>
        </div>
        <input type="text" className={styles.description} value={description} disabled />
      </div>
    </div>
  );
}
